import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import config, { whatsappLink } from "../config";
import logo from "../assets/logo.png";

const links = [
  { to: "/produktet", label: "Produktet" },
  { to: "/rreth-nesh", label: "Rreth nesh" },
  { to: "/pyetje", label: "Pyetje" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <Bar $scrolled={scrolled || open}>
        <Inner>
          <Brand to="/" aria-label={config.brand.name}>
            <img src={logo} alt="" width="44" height="44" />
            <span>{config.brand.name}</span>
          </Brand>

          <Links>
            {links.map((l) => (
              <Item key={l.to} to={l.to}>
                {l.label}
              </Item>
            ))}
          </Links>

          <Right>
            <Order
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
            >
              Porosit
            </Order>
            <Burger
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Mbyll menunë" : "Hap menunë"}
              aria-expanded={open}
            >
              <span />
              <span />
            </Burger>
          </Right>
        </Inner>
      </Bar>

      <Drawer $open={open} aria-hidden={!open}>
        <nav>
          {links.map((l) => (
            <DrawerLink key={l.to} to={l.to}>
              {l.label}
            </DrawerLink>
          ))}
        </nav>
        <DrawerOrder
          href={whatsappLink()}
          target="_blank"
          rel="noopener noreferrer"
        >
          Porosit në WhatsApp
        </DrawerOrder>
      </Drawer>
    </>
  );
}

const Bar = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 100;
  transition:
    background 0.4s ease,
    border-color 0.4s ease,
    backdrop-filter 0.4s ease;
  border-bottom: 1px solid transparent;

  ${({ $scrolled, theme }) =>
    $scrolled &&
    css`
      background: rgba(251, 250, 247, 0.82);
      backdrop-filter: blur(14px) saturate(1.3);
      -webkit-backdrop-filter: blur(14px) saturate(1.3);
      border-bottom-color: ${theme.colors.line};
    `}
`;

const Inner = styled.div`
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  ${({ theme }) => theme.bp.md} {
    padding: 18px 40px;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 9px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.size.lg};
  letter-spacing: -0.02em;
  flex-shrink: 0;

  /* Logoja është PNG transparent — pa radius dhe pa sfond.
     Vizatimi është i hollë, prandaj mbahet 40px+ që të lexohet. */
  img {
    width: 40px;
    height: 40px;
    object-fit: contain;

    ${({ theme }) => theme.bp.md} {
      width: 44px;
      height: 44px;
    }
  }
`;

const Links = styled.nav`
  display: none;
  gap: 36px;

  ${({ theme }) => theme.bp.md} {
    display: flex;
  }
`;

const Item = styled(NavLink)`
  font-size: ${({ theme }) => theme.size.sm};
  color: ${({ theme }) => theme.colors.inkSoft};
  position: relative;
  padding: 4px 0;
  transition: color 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    height: 1px;
    width: 0;
    background: currentColor;
    transition: width 0.4s ${({ theme }) => theme.ease};
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.colors.ink};
  }
  &:hover::after,
  &.active::after {
    width: 100%;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Order = styled.a`
  display: none;
  padding: 11px 24px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.ink};
  color: #fff;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: 500;
  transition:
    background 0.3s ease,
    transform 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.brandDeep};
    transform: translateY(-1px);
  }

  ${({ theme }) => theme.bp.sm} {
    display: inline-flex;
  }
`;

const Burger = styled.button`
  width: 40px;
  height: 40px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;

  span {
    display: block;
    width: 20px;
    height: 1.5px;
    background: ${({ theme }) => theme.colors.ink};
    border-radius: 2px;
  }

  ${({ theme }) => theme.bp.md} {
    display: none;
  }
`;

const Drawer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99;
  background: ${({ theme }) => theme.colors.bg};
  padding: 110px 28px 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition:
    opacity 0.4s ease,
    visibility 0.4s ease;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};

  nav {
    display: flex;
    flex-direction: column;
  }

  ${({ theme }) => theme.bp.md} {
    display: none;
  }
`;

const DrawerLink = styled(NavLink)`
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.size.h3};
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};

  &.active {
    color: ${({ theme }) => theme.colors.brand};
  }
`;

const DrawerOrder = styled.a`
  display: block;
  text-align: center;
  padding: 17px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.ink};
  color: #fff;
  font-weight: 500;
`;
